import { Card, CardBody } from "@nextui-org/react";
import React, { useMemo, useState } from "react";
import ClientDataGrid from "../../../../elements/ClientDataGrid";
import ReactSelect from "../../../../elements/ReactSelect";
import { getFetcher } from "../../../../../services/api";
import toast from "react-hot-toast";
import { formatNumber } from "../../../../../utils/formatter.utils";

export default function B3Table() {
  const currentYear = new Date().getFullYear();
  const currentMonth = new Date().getMonth() + 1;
  const [startMonth, setStartMonth] = useState(currentMonth);
  const [startYear, setStartYear] = useState(currentYear);
  const [endMonth, setEndMonth] = useState(currentMonth);
  const [endYear, setEndYear] = useState(currentYear);
  const [bahanB3TimeRange, setBahanB3TimeRange] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const fetchDataByTimeRange = async () => {
    try {
      setIsLoading(true);
      if (!startYear || !endYear || !startMonth || !endMonth || startYear > endYear || (startYear === endYear && startMonth > endMonth)) {
        toast.error("Tanggal awal harus lebih kecil atau sama dengan tanggal akhir!");
        return;
      }
      const data = await getFetcher(`/api/dashboard/admin/pengelolaan-bahan-b3?startMonth=${startMonth}&startYear=${startYear}&endMonth=${endMonth}&endYear=${endYear}`);
      const processedData = data?.data?.map((item) => ({
        ...item,
        id: item.bahan_b3_id, // Gunakan bahan_b3_id sebagai id
      }));
      setBahanB3TimeRange(processedData);
    } catch (error) {
      console.error("Error fetching B3 data:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const columns = useMemo(
    () => [
      /*
       * Lebar kolom memakai flex, bukan autosize. Nama bahan kimia bisa 160
       * karakter lebih; autosize dengan includeOutliers membuat kolom itu
       * ribuan piksel dan tabel harus digeser mendatar. Dengan flex, keenam
       * kolom berbagi lebar wadah, dan nama panjang dibungkus ke baris baru.
       */
      { field: "nama_bahan_kimia", headerName: "Nama Bahan Kimia", flex: 2.2, minWidth: 180, valueGetter: (value, row) => row?.nama_bahan_kimia ?? "-" },
      { field: "pembelian_lokal", type: "number", headerName: "Pembelian Lokal", flex: 1, minWidth: 110, valueGetter: (value, row) => row?.pembelian_lokal ?? 0, valueFormatter: (value) => formatNumber(value) },
      { field: "pembelian_impor", type: "number", headerName: "Pembelian Impor", flex: 1, minWidth: 110, valueGetter: (value, row) => row?.pembelian_impor ?? 0, valueFormatter: (value) => formatNumber(value) },
      { field: "penggunaan", type: "number", headerName: "Penggunaan", flex: 1, minWidth: 100, valueGetter: (value, row) => row?.penggunaan ?? 0, valueFormatter: (value) => formatNumber(value) },
      { field: "produksi", type: "number", headerName: "Produksi", flex: 1, minWidth: 100, valueGetter: (value, row) => row?.produksi ?? 0, valueFormatter: (value) => formatNumber(value) },
      { field: "distribusi", type: "number", headerName: "Distribusi", flex: 1, minWidth: 100, valueGetter: (value, row) => row?.distribusi ?? 0, valueFormatter: (value) => formatNumber(value) },
    ],
    []
  );

  return (
    <Card radius="sm">
      <CardBody>
        <div className="flex flex-col md:flex-row md:justify-between items-center mb-4">
          <div className="flex flex-col md:flex-row items-center space-x-4">
            <ReactSelect
              label="Bulan Awal"
              data={Array.from({ length: 12 }, (_, i) => ({
                value: i + 1,
                label: new Date(0, i).toLocaleString("id-ID", { month: "long" }),
              }))}
              value={startMonth}
              onChange={(selected) => setStartMonth(selected?.value)}
              defaultValue={{ value: startMonth, label: new Date(0, startMonth - 1).toLocaleString("id-ID", { month: "long" }) }}
              isMulti={false}
            />
            <ReactSelect
              label="Tahun Awal"
              data={Array.from({ length: 15 }, (_, i) => ({
                value: currentYear - i,
                label: currentYear - i,
              }))}
              value={startYear}
              onChange={(selected) => setStartYear(selected?.value)}
              defaultValue={{ value: startYear, label: startYear }}
              isMulti={false}
            />
          </div>
          <div className="flex flex-col md:flex-row items-center space-x-4 mt-4 md:mt-0">
            <ReactSelect
              label="Bulan Akhir"
              data={Array.from({ length: 12 }, (_, i) => ({
                value: i + 1,
                label: new Date(0, i).toLocaleString("id-ID", { month: "long" }),
              }))}
              value={endMonth}
              onChange={(selected) => setEndMonth(selected?.value)}
              defaultValue={{ value: endMonth, label: new Date(0, endMonth - 1).toLocaleString("id-ID", { month: "long" }) }}
              isMulti={false}
            />
            <ReactSelect
              label="Tahun Akhir"
              data={Array.from({ length: 15 }, (_, i) => ({
                value: currentYear - i,
                label: currentYear - i,
              }))}
              value={endYear}
              onChange={(selected) => setEndYear(selected?.value)}
              defaultValue={{ value: endYear, label: endYear }}
              isMulti={false}
            />
          </div>
          <button className="bg-blue-500 text-white px-4 py-2 rounded mt-4 md:mt-0" onClick={fetchDataByTimeRange}>
            Apply
          </button>
        </div>
        <div className="h-[500px]">
          <ClientDataGrid
            columns={columns}
            rows={bahanB3TimeRange}
            isLoading={isLoading}
            autoResize={false}
            getRowHeight={() => "auto"}
            ukurUlangTinggiBaris
            sx={{
              fontSize: "0.8rem",
              // MUI menyisakan ruang bilah gulir tegak (~5px) di lebar isi
              // walau tidak ada gulir; kolomnya flex, jadi sisa itu boleh
              // disembunyikan tanpa ada yang terpotong.
              "& .MuiDataGrid-virtualScroller": { overflowX: "hidden" },
              "& .MuiDataGrid-columnHeaderTitle": { fontSize: "0.78rem", fontWeight: 600, whiteSpace: "normal", lineHeight: 1.2 },
              "& .MuiDataGrid-cell": { whiteSpace: "normal", lineHeight: 1.3, paddingTop: "6px", paddingBottom: "6px", alignItems: "center" },
              "& .MuiDataGrid-cell--textRight": { fontVariantNumeric: "tabular-nums" },
            }}
            // Kolom bertipe number diekspor sebagai angka mentah. Tanpa ini
            // serializer CSV MUI memakai nilai terformat, sehingga berkas hasil
            // ekspor berisi "1.234.567" alih-alih 1234567.
            slotProps={{ toolbar: { csvOptions: { ignoreValueFormatter: true } } }}
          />
        </div>
      </CardBody>
    </Card>
  );
}
